import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'rsuite';
import { has, get, set, map, split, size, isEmpty } from 'lodash/fp';
import {
  fetchClientColumns,
  fetchClients as fetchGuests
} from '../../../redux/sagas/client/routines';
import {
  getClientColumnsByName,
  getIsFetchingClientColumns
} from '../../../redux/selectors/client';
import {
  getOrder,
  getQuery as getGuestsQuery,
  getIsFetching as getIsFetchingGuests
} from '../../../redux/selectors/components/guest-list';
import {
  getPaginationWithoutPages,
  getPaginationLoadedPages,
  getPaginationQueryOptions,
  getCurrentPageItems as getGuests
} from '../../../redux/selectors/components/guest-list/pagination';
import {
  clearGuests,
  guestsOnChangePage,
  guestsOnChangeOrder
} from '../../../redux/actions';
import Pagination from '../../Pagination';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import paginationAndSearchQuery from '../../../helpers/paginationAndSearchQuery';
import AutoHeightTable from '../../AutoHeightTable';
import GuestFieldCell from '../../widgets/custom-cell/GuestFieldCell';

const { Column } = Table;
const SelectedColumnBackground = '#cce9ff';

const GuestList = ({ columns, selectedColumn, onSelectItem }) => {
  const dispatch = useDispatch();
  const guests = useSelector(getGuests);
  const order = useSelector(getOrder);
  const [sortColumn, sortType] = split(':', order);
  const searchQuery = useSelector(getGuestsQuery);
  const paginationQuery = useSelector(getPaginationQueryOptions);
  const query = {
    ...paginationAndSearchQuery(paginationQuery, searchQuery),
    order
  };
  const pagination = useSelector(getPaginationWithoutPages);
  const loadedPages = useSelector(getPaginationLoadedPages);
  const columnSettingsByName = useSelector(getClientColumnsByName);
  const isFetchingGuests = useSelector(getIsFetchingGuests);
  const isFetchingGuestColumns = useSelector(getIsFetchingClientColumns);
  const isFetching = isFetchingGuestColumns || isFetchingGuests;
  const rowCount = size(guests);

  useEffect(
    () => {
      if (isEmpty(columnSettingsByName)) {
        dispatch(fetchClientColumns());
      }
      return function reset() {
        dispatch(guestsOnChangePage(1));
      };
    },
    [columnSettingsByName]
  );

  const handleSortColumn = (column, type) => {
    const newOrder = `${column}:${type}`;
    dispatch(clearGuests());
    dispatch(fetchGuests({ ...query, order: newOrder }));
    dispatch(guestsOnChangeOrder(newOrder));
  };

  const handleChangePage = page => {
    const isPageLoaded = has(page, loadedPages);
    const newQuery = set('page', page, query);
    if (!isPageLoaded) dispatch(fetchGuests(newQuery));
    else dispatch(guestsOnChangePage(page));
  };

  const handleChangeLimit = limit => {
    const newQuery = { ...query, limit, page: 1 };
    dispatch(clearGuests());
    dispatch(fetchGuests(newQuery));
  };

  return (
    <Fragment>
      <AutoHeightTable
        bordered
        data={guests}
        loading={isFetching}
        sortType={sortType}
        sortColumn={sortColumn}
        onSortColumn={handleSortColumn}
        onRowClick={onSelectItem}
        reduceHeight={540}
        style={{ marginTop: 20 }}
      >
        {map(column => {
          const columnSettings = get(column, columnSettingsByName);
          return (
            <Column key={column} width={160} sortable resizable>
              <CustomHeaderCell
                background={
                  column === selectedColumn ? SelectedColumnBackground : ''
                }
              >
                {get('label', columnSettings)}
              </CustomHeaderCell>
              <GuestFieldCell
                dataKey={column}
                options={get('options', columnSettings)}
              />
            </Column>
          );
        }, columns)}
      </AutoHeightTable>
      {rowCount > 0 && (
        <Pagination
          page={pagination.currentPage}
          limit={pagination.limit}
          total={pagination.totalCount}
          onChangePage={handleChangePage}
          onChangeLimit={handleChangeLimit}
        />
      )}
    </Fragment>
  );
};

GuestList.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedColumn: PropTypes.string,
  onSelectItem: PropTypes.func.isRequired
};

GuestList.defaultProps = {
  selectedColumn: ''
};

export default GuestList;
